/** Game-specific components: timer ring, vote bars, phone call, artefacts, leaderboard, dimensions. */
import { formatDuration, formatNumber } from "@shared/i18n";
import type { PublicPlayer } from "@shared/protocol";
import type { Scenario } from "@shared/scenario";
import { Fragment, type JSX } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { useI18n, useT } from "../i18n";
import type { PlayerCall, PlayerNarrative, RevealCall } from "../lib/scenario";
import { hasIcon, Icon } from "./Icon";
import { QualityPill } from "./ui";

/* ---------- Timer ring ---------- */

export function TimerRing({
  closesAt,
  durationSec,
  now,
  size = 96,
}: {
  closesAt: number;
  durationSec: number;
  now: number;
  size?: number;
}) {
  const { locale } = useI18n();
  const remaining = Math.max(0, closesAt - now);
  const total = Math.max(1, durationSec * 1000);
  const frac = Math.min(1, remaining / total);
  const r = 44;
  const c = 2 * Math.PI * r;
  const cls = remaining <= 10_000 ? "critical" : remaining <= 20_000 ? "warn" : "";
  return (
    <div class={`timer ${cls}`} style={{ "--timer-size": `${size}px` } as never} role="timer" aria-live="off">
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle class="track" cx="50" cy="50" r={r} fill="none" stroke-width="8" />
        <circle
          class="progress"
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke-width="8"
          stroke-linecap="round"
          stroke-dasharray={c}
          stroke-dashoffset={c * (1 - frac)}
        />
      </svg>
      <span class="value">{formatDuration(locale, remaining)}</span>
    </div>
  );
}

/* ---------- Vote bars ---------- */

export interface BarOption {
  id: string;
  label: string;
  quality?: "best" | "good" | "risky" | "bad";
}

export function VoteBars({
  options,
  tally,
  winnerId,
  showQuality,
  mine,
}: {
  options: BarOption[];
  tally: Record<string, number> | null;
  winnerId?: string | null;
  showQuality?: boolean;
  mine?: string | null;
}) {
  const t = useT();
  const { locale } = useI18n();
  const total = options.reduce((a, o) => a + (tally?.[o.id] ?? 0), 0);
  return (
    <div class="bars">
      {options.map((o) => {
        const n = tally?.[o.id] ?? 0;
        const pct = total ? (n / total) * 100 : 0;
        const isWinner = winnerId === o.id;
        return (
          <div
            key={o.id}
            class={`bar ${isWinner ? "winner" : ""} ${showQuality && o.quality ? `q-${o.quality}` : ""}`}
          >
            <div class="label">
              {isWinner ? <Icon name="flag-triangle-right" /> : null}
              <span>{o.label}</span>
              {showQuality && o.quality ? <QualityPill quality={o.quality} /> : null}
              {mine === o.id ? (
                <span class="pill accent">
                  <Icon name="check" /> {t("player.result.youChose")}
                </span>
              ) : null}
            </div>
            <div class="count">{tally ? formatNumber(locale, n) : "—"}</div>
            <div
              class="track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(pct)}
            >
              <div class="fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Phone call ---------- */

/**
 * A call as a caption log: one full-width row per line, the speaker named above it and marked by the
 * colour of its start rule. Bubbles are kept for text messages and chat artefacts, so a call never
 * reads as a chat thread. `compact` tightens the spacing for narrow surfaces (the player's phone, the
 * landing hero) but still names every speaker, because without sides the label is the only cue.
 */
export function PhoneCall({
  node,
  revealedLines,
  reveal,
  compact,
  animate = true,
}: {
  node: PlayerCall;
  revealedLines: number;
  reveal?: RevealCall | null;
  compact?: boolean;
  animate?: boolean;
}) {
  const t = useT();
  const lines = node.transcript.slice(0, Math.max(0, revealedLines));
  const live = revealedLines < node.transcript.length;
  const tells = useMemo(() => new Map((reveal?.tells ?? []).map((x) => [x.index, x.tell])), [reveal]);
  return (
    <section class={`phone ${compact ? "compact" : ""}`} aria-label={t("player.call.title")}>
      <div class="caller-head">
        <div class="avatar" aria-hidden="true">
          <Icon name="phone" class="lg" />
        </div>
        <div>
          <div class="eyebrow phone-label">{t("screen.callFrom")}</div>
          <div class="name">{node.caller.name}</div>
          <div class="number">{node.caller.displayNumber}</div>
        </div>
        {live && animate ? (
          <div class="wave" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        ) : null}
      </div>
      <div class="transcript" aria-live="polite" aria-relevant="additions">
        {lines.map((l, i) => (
          <div key={i} class={`line ${l.speaker} ${animate ? "fade-in" : ""}`}>
            {/* A system line is stage direction ("the line goes quiet"): it has no speaker unless named. */}
            {l.speaker !== "system" || l.name ? (
              <span class="who eyebrow">
                {l.name ?? (l.speaker === "caller" ? node.caller.name : node.callee.name)}
              </span>
            ) : null}
            <span class="said">{l.text}</span>
            {tells.get(i) ? (
              <span class="tell">
                <strong>{t("screen.tells")}:</strong> {tells.get(i)}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {reveal?.redFlags?.length ? (
        <div style={{ marginTop: "var(--s-4)" }} class="fade-in">
          <div class="eyebrow phone-flags">
            <Icon name="alert-triangle" /> {t("screen.redFlags")}
          </div>
          <ul class="phone-flag-list">
            {reveal.redFlags.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
          {reveal.caller.spoofed ? (
            <p class="small phone-label">
              <Icon name="info" /> {t("screen.spoofed")}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/* ---------- Artefact ----------
 * One card, seven skins. The data contract is the same for every kind (from/to/subject/timestamp/
 * body); only the chrome changes, so that a room reacts to an email like an email and to a text like
 * a text. Each skin is a generic, unbranded client: a webmail reading pane, a phone thread, a
 * workplace chat channel, a helpdesk ticket, a news page, a paper memo and a system alert.
 */

type Artifact = NonNullable<PlayerNarrative["artifact"]>;
/** Mirrors the scenario schema's enum; the client type widens `kind` to string. */
type ArtifactKind = "email" | "sms" | "chat" | "ticket" | "news" | "memo" | "alert";

const ARTIFACT_KIND_KEY: Record<ArtifactKind, string> = {
  email: "artifact.kind.email",
  sms: "artifact.kind.sms",
  chat: "artifact.kind.chat",
  ticket: "artifact.kind.ticket",
  news: "artifact.kind.news",
  memo: "artifact.kind.memo",
  alert: "artifact.kind.alert",
};

/** Splits "Dana Voss <dana@example.com>" into a display name and an address; anything else is a name. */
function splitAddress(from: string): { name: string; address?: string } {
  const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(from);
  if (m?.[1]) return { name: m[1], address: m[2] };
  if (m?.[2]) return { name: m[2] };
  return { name: from };
}

/** Offers a line break after "@" so a long address wraps between mailbox and domain, not mid-word. */
function breakable(text: string): JSX.Element[] {
  return text.split(/(?<=@)/).map((part, i) => (
    <Fragment key={i}>
      {i ? <wbr /> : null}
      {part}
    </Fragment>
  ));
}

/** A phone number reads as a number, not as a name with an initial. */
const PHONE_NUMBER = /^[+(\d][\d\s().+-]*$/;

/** The first letter of a name, for an avatar; undefined when there is none (numbers, emoji, "@…"). */
function initialOf(name: string | undefined): string | undefined {
  if (!name || PHONE_NUMBER.test(name.trim())) return undefined;
  const ch = /[\p{L}\p{N}]/u.exec(name)?.[0];
  return ch ? ch.toLocaleUpperCase() : undefined;
}

/** Blank lines separate messages, so a thread can carry several bubbles from one sender. */
function messagesOf(body: string): string[] {
  const parts = body
    .split(/\n[ \t]*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length ? parts : [body];
}

function Avatar({ name, icon, square }: { name?: string; icon: string; square?: boolean }) {
  const initial = initialOf(name);
  return (
    <span class={`artifact-avatar${square ? " square" : ""}`} aria-hidden="true">
      {initial ?? <Icon name={icon} />}
    </span>
  );
}

function Stamp({ value }: { value?: string }) {
  return value ? <span class="artifact-time mono">{value}</span> : null;
}

/** A labelled row for the memo and ticket field grids. */
function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>
        <bdi>{value}</bdi>
      </dd>
    </>
  );
}

function EmailArtifact({ a }: { a: Artifact }) {
  const t = useT();
  const sender = a.from ? splitAddress(a.from) : undefined;
  return (
    <>
      <div class="artifact-head">
        {a.subject ? <h3 class="artifact-title">{a.subject}</h3> : null}
        <div class="artifact-sender">
          <Avatar name={sender?.name} icon="mail" />
          <div class="artifact-who">
            <div class="artifact-from">
              {sender ? (
                <strong class="artifact-name">
                  <span class="visually-hidden">{t("artifact.from")} </span>
                  <bdi>{sender.name}</bdi>
                </strong>
              ) : null}
              <Stamp value={a.timestamp} />
            </div>
            {sender?.address ? (
              <span class="artifact-address">
                <bdi dir="ltr">&lt;{breakable(sender.address)}&gt;</bdi>
              </span>
            ) : null}
            {a.to ? (
              <span class="artifact-to">
                <span class="artifact-label">{t("artifact.to")}</span>{" "}
                <span class="artifact-address">
                  <bdi>{breakable(a.to)}</bdi>
                </span>
              </span>
            ) : null}
          </div>
        </div>
      </div>
      <div class="artifact-body">{a.body}</div>
    </>
  );
}

function SmsArtifact({ a }: { a: Artifact }) {
  const t = useT();
  return (
    <>
      <div class="artifact-head">
        <Avatar name={a.from} icon="user" />
        {a.from ? (
          <strong class="artifact-name">
            <bdi>{a.from}</bdi>
          </strong>
        ) : null}
        {a.to ? (
          <span class="artifact-to">
            <span class="artifact-label">{t("artifact.to")}</span> <bdi>{a.to}</bdi>
          </span>
        ) : null}
      </div>
      <div class="artifact-thread">
        <Stamp value={a.timestamp} />
        {messagesOf(a.body).map((m, i) => (
          <p class="artifact-bubble" key={i}>
            {m}
          </p>
        ))}
      </div>
    </>
  );
}

function ChatArtifact({ a }: { a: Artifact }) {
  const t = useT();
  // Scenarios name the room in either field ("#finance-leads" as `to`, or "#general" as `subject`);
  // whichever looks like a channel heads the window and the other one rides along as context.
  const channel = [a.to, a.subject].find((v) => v?.trim().startsWith("#"));
  const title = channel ?? a.subject ?? a.to ?? t(ARTIFACT_KIND_KEY.chat);
  const context = [a.subject, a.to].filter((v) => v && v !== title).join(" · ");
  return (
    <>
      <div class="artifact-head">
        {channel ? (
          <span class="artifact-channel-glyph" aria-hidden="true">
            #
          </span>
        ) : (
          <Icon name="message-square" class="artifact-channel-icon" />
        )}
        <strong class="artifact-channel">
          <bdi>{channel ? channel.trim().slice(1) : title}</bdi>
        </strong>
        {context ? (
          <span class="artifact-context">
            <bdi>{context}</bdi>
          </span>
        ) : null}
      </div>
      <div class="artifact-thread">
        <div class="artifact-message">
          <Avatar name={a.from} icon="user" square />
          <div class="artifact-message-main">
            <div class="artifact-message-meta">
              {a.from ? (
                <strong class="artifact-name">
                  <bdi>{a.from}</bdi>
                </strong>
              ) : null}
              <Stamp value={a.timestamp} />
            </div>
            {messagesOf(a.body).map((m, i) => (
              <p class="artifact-bubble" key={i}>
                {m}
              </p>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function TicketArtifact({ a }: { a: Artifact }) {
  const t = useT();
  return (
    <>
      <div class="artifact-head">
        <div class="artifact-eyebrow">
          <span class="eyebrow">
            <Icon name="ticket" /> {t(ARTIFACT_KIND_KEY.ticket)}
          </span>
          <Stamp value={a.timestamp} />
        </div>
        {a.subject ? <h3 class="artifact-title">{a.subject}</h3> : null}
        {a.from || a.to ? (
          <dl class="artifact-fields">
            <Field label={t("artifact.requester")} value={a.from} />
            <Field label={t("artifact.to")} value={a.to} />
          </dl>
        ) : null}
      </div>
      <div class="artifact-body">{a.body}</div>
    </>
  );
}

function NewsArtifact({ a }: { a: Artifact }) {
  const t = useT();
  const byline = [a.from, a.timestamp].filter(Boolean).join(" · ");
  return (
    <>
      <div class="artifact-head">
        <div class="artifact-masthead eyebrow">{t(ARTIFACT_KIND_KEY.news)}</div>
        {a.subject ? <h3 class="artifact-title">{a.subject}</h3> : null}
        {byline ? <p class="artifact-byline">{byline}</p> : null}
      </div>
      <div class="artifact-body">{a.body}</div>
    </>
  );
}

function MemoArtifact({ a }: { a: Artifact }) {
  const t = useT();
  return (
    <>
      <div class="artifact-head">
        <div class="artifact-memo-mark">{t(ARTIFACT_KIND_KEY.memo)}</div>
        <dl class="artifact-fields">
          <Field label={t("artifact.to")} value={a.to} />
          <Field label={t("artifact.from")} value={a.from} />
          <Field label={t("artifact.date")} value={a.timestamp} />
          <Field label={t("artifact.subject")} value={a.subject} />
        </dl>
      </div>
      <div class="artifact-body">{a.body}</div>
    </>
  );
}

function AlertArtifact({ a }: { a: Artifact }) {
  const t = useT();
  return (
    <>
      <div class="artifact-head">
        <Icon name="alert-triangle" class="artifact-alert-icon" />
        <div class="artifact-who">
          <h3 class="artifact-title">{a.subject ?? t(ARTIFACT_KIND_KEY.alert)}</h3>
          {a.from ? (
            <span class="artifact-context">
              <bdi>{a.from}</bdi>
            </span>
          ) : null}
        </div>
      </div>
      <div class="artifact-body">{a.body}</div>
      {a.to || a.timestamp ? (
        <div class="artifact-foot">
          {a.to ? (
            <span class="artifact-to">
              <span class="artifact-label">{t("artifact.to")}</span>{" "}
              <span class="artifact-address">
                <bdi>{breakable(a.to)}</bdi>
              </span>
            </span>
          ) : null}
          <Stamp value={a.timestamp} />
        </div>
      ) : null}
    </>
  );
}

const ARTIFACT_SKIN: Record<ArtifactKind, (props: { a: Artifact }) => JSX.Element> = {
  email: EmailArtifact,
  sms: SmsArtifact,
  chat: ChatArtifact,
  ticket: TicketArtifact,
  news: NewsArtifact,
  memo: MemoArtifact,
  alert: AlertArtifact,
};

export function ArtifactCard({ artifact }: { artifact: Artifact }) {
  const t = useT();
  // An unknown kind (a newer scenario on an older client) falls back to the plainest skin.
  const kind: ArtifactKind = artifact.kind in ARTIFACT_SKIN ? (artifact.kind as ArtifactKind) : "memo";
  const Skin = ARTIFACT_SKIN[kind];
  return (
    <div class={`artifact artifact--${kind}`} role="group" aria-label={t(ARTIFACT_KIND_KEY[kind])}>
      <Skin a={artifact} />
    </div>
  );
}

/* ---------- Leaderboard ---------- */

export function Leaderboard({
  players,
  roles,
  meId,
  limit = 10,
}: {
  players: PublicPlayer[];
  roles: { id: string; name: string }[];
  meId?: string | null;
  limit?: number;
}) {
  const { locale } = useI18n();
  const roleName = (id: string | null) => roles.find((r) => r.id === id)?.name ?? "";
  const sorted = [...players].sort((a, b) => b.score - a.score);
  const top = sorted.slice(0, limit);
  const meIndex = meId ? sorted.findIndex((p) => p.id === meId) : -1;
  const rows = meIndex >= limit ? [...top, sorted[meIndex]!] : top;
  return (
    <ol class="leaderboard" aria-label="Leaderboard">
      {rows.map((p) => {
        const rank = sorted.indexOf(p) + 1;
        return (
          <li key={p.id} class={`lb-row ${p.id === meId ? "me" : ""} ${p.connected ? "" : "offline"}`}>
            <span class="rank">{rank}</span>
            <span class="avatar" aria-hidden="true">
              {p.avatar}
            </span>
            <span>
              <span class="name">
                <bdi>{p.name}</bdi>
                {p.simulated ? <SimMark /> : null}
              </span>
              {p.roleId ? <div class="role">{roleName(p.roleId)}</div> : null}
            </span>
            <span class="score">{formatNumber(locale, p.score)}</span>
          </li>
        );
      })}
    </ol>
  );
}

/* ---------- Simulated participant mark ---------- */

/** A small, consistent marker wherever a simulated participant's name appears. */
export function SimMark() {
  const t = useT();
  return (
    <span class="sim-mark" title={t("sim.label")}>
      <Icon name="bot" />
      <span class="visually-hidden">{t("sim.label")}</span>
    </span>
  );
}

/* ---------- Dimensions ---------- */

export function Dimensions({
  dimensions,
  scores,
}: {
  dimensions: Scenario["dimensions"];
  scores: Record<string, number | null>;
}) {
  return (
    <div class="dims">
      {dimensions.map((d) => {
        const v = scores[d.id];
        return (
          <div key={d.id} class="dim" title={d.description}>
            <span class="name">{d.label}</span>
            <span class="val">{v === null || v === undefined ? "—" : `${v}%`}</span>
            <span class="meter" aria-hidden="true">
              <i
                style={{
                  width: `${v ?? 0}%`,
                  background:
                    v === null || v === undefined
                      ? "transparent"
                      : v >= 75
                        ? "var(--best)"
                        : v >= 50
                          ? "var(--good)"
                          : v >= 25
                            ? "var(--risky)"
                            : "var(--bad)",
                }}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Radar (SVG) for the debrief ---------- */

export function Radar({
  dimensions,
  scores,
  size = 260,
}: {
  dimensions: Scenario["dimensions"];
  scores: Record<string, number | null>;
  size?: number;
}) {
  const n = dimensions.length;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 64;
  const pt = (i: number, v: number) => {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + Math.cos(a) * r * v, cy + Math.sin(a) * r * v] as const;
  };
  const rings = [0.25, 0.5, 0.75, 1];
  const poly = dimensions.map((d, i) => pt(i, ((scores[d.id] ?? 0) as number) / 100).join(",")).join(" ");
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label="Capability radar">
      {rings.map((v) => (
        <polygon
          key={v}
          points={dimensions.map((_, i) => pt(i, v).join(",")).join(" ")}
          fill="none"
          stroke="var(--line)"
          stroke-width="1"
        />
      ))}
      {dimensions.map((_, i) => {
        const [x, y] = pt(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line)" />;
      })}
      <polygon
        points={poly}
        fill="var(--accent)"
        fill-opacity="0.25"
        stroke="var(--accent)"
        stroke-width="2.5"
      />
      {dimensions.map((d, i) => {
        const [x, y] = pt(i, 1.24);
        return (
          <text
            key={d.id}
            x={x}
            y={y}
            text-anchor="middle"
            dominant-baseline="middle"
            font-size="12"
            fill="var(--fg-2)"
            font-weight="700"
          >
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}

/* ---------- Role badge ---------- */

export function RoleBadge({ role }: { role: { name: string; icon: string } | null | undefined }) {
  const t = useT();
  if (!role) return <span class="pill">{t("player.lobby.noRole")}</span>;
  return (
    <span class="pill accent">
      <Icon name={hasIcon(role.icon) ? role.icon : "user"} /> {role.name}
    </span>
  );
}

/* ---------- Floating reactions ---------- */

export function ReactionsLayer({ subscribe }: { subscribe: (fn: (emoji: string) => void) => () => void }) {
  const [items, setItems] = useState<{ id: number; emoji: string; x: number }[]>([]);
  useEffect(() => {
    let seq = 0;
    return subscribe((emoji) => {
      const id = ++seq;
      setItems((cur) => [...cur.slice(-14), { id, emoji, x: Math.random() * 60 }]);
      setTimeout(() => setItems((cur) => cur.filter((i) => i.id !== id)), 2600);
    });
  }, [subscribe]);
  return (
    <div class="reactions-float" aria-hidden="true">
      {items.map((i) => (
        <span key={i.id} class="reaction" style={{ insetInlineEnd: `${i.x}px` }}>
          {i.emoji}
        </span>
      ))}
    </div>
  );
}
